<template>
  <v-layout column>
    <attach-payment-dialog v-model="showAttachDialog"
                           @refresh="$emit('reload')"
                           :load-item="loadItem"
                           :strategy="attachPaymentStrategy">
    </attach-payment-dialog>

      <v-toolbar :extended="isMobile" :dense="!isMobile" flat>
        <v-icon color="green"
                v-if="(invoice && verifiedCustomer) || (bill && verifiedVendor)">
          verified_user
        </v-icon>
        <v-toolbar-title color="primary">
          {{ invoice ? customer.name : vendor.name }}
        </v-toolbar-title>
        <v-spacer/>

        <template :slot="isMobile ? 'extension' : 'default'">
          <v-toolbar-items slot="toolbarItems">
            <template v-if="invoice">
              <v-quickbooks-billing-btn :transaction="transaction"
                                        :invoicing-company="company"
                                        :disabled="false"
                                        :loading="false">
              </v-quickbooks-billing-btn>
              <v-btn flat
                     @click="openAttachDialog(transaction, ATTACH_INVOICE)"
                     color="accent">
                <v-layout column>
                  <v-icon>receipt</v-icon>
                  Invoiced
                </v-layout>
              </v-btn>
              <v-btn flat
                     @click="openAttachDialog(transaction, ACK_PAYMENT)"
                     color="accent">
                <v-layout column>
                  <v-icon>check_circle</v-icon>
                  Paid
                </v-layout>
              </v-btn>
            </template>
            <template v-if="bill">
              <v-btn flat
                     @click="openAttachDialog(transaction, APPLY_PAYMENT)"
                     color="accent">
                <v-layout column>
                  <v-icon>attach_money</v-icon>
                  Pay
                </v-layout>
              </v-btn>
            </template>
            <v-btn v-if="canCancelTransaction"
                   @click="cancelTransaction()" flat color="accent">
              <v-layout column>
                <v-icon>highlight_off</v-icon>
                Cancel
              </v-layout>
            </v-btn>
          </v-toolbar-items>
        </template>
      </v-toolbar>

    <v-card flat class="green darken-3">
      <v-stepper :value="paymentProgressStatusNumber"
                 alt-labels class="my-3 pt-2 arrow-stepper white">
        <v-stepper-header>
          <v-stepper-step step="1" class="py-0"
                          :complete="paymentProgressStatusNumber > 1">
            Open
          </v-stepper-step>

          <v-divider :class="{'completed': paymentProgressStatusNumber > 1}"></v-divider>

          <v-stepper-step step="2" class="py-0"
                          :complete="paymentProgressStatusNumber >= 2">
            Invoiced
            <small v-if="!isMobile && invoiceTime">
              {{invoiceTime | formatDate}}
            </small>
          </v-stepper-step>

          <v-divider :class="{'completed': paymentProgressStatusNumber >= 2}"></v-divider>

          <v-stepper-step step="3" class="py-0"
                          :complete="paymentProgressStatusNumber > 3">
            Remitted Payment
            <small v-if="!isMobile && paymentTime">
              {{paymentTime | formatDate}}
            </small>
          </v-stepper-step>

          <v-divider :class="{'completed': paymentProgressStatusNumber > 3}"></v-divider>

          <v-stepper-step step="4" class="py-0"
                          :complete="paymentProgressStatusNumber === 4">
            Received Payment
            <small v-if="!isMobile && receivedPaymentTime">
              {{receivedPaymentTime | formatDate}}
            </small>
          </v-stepper-step>
        </v-stepper-header>
      </v-stepper>
    </v-card>

    <v-list>
      <v-subheader v-if="bill">Bill Overview ({{transaction.status}})</v-subheader>
      <v-subheader v-if="invoice">Invoice Overview ({{transaction.status}})</v-subheader>
      <v-list-tile>
        <v-list-tile-avatar>
          <v-icon>attach_money</v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title>${{transaction.amount}}</v-list-tile-title>
          <v-list-tile-sub-title>{{transaction.description}}</v-list-tile-sub-title>
        </v-list-tile-content>
      </v-list-tile>
      <v-list-tile>
        <v-list-tile-avatar>
          <v-icon>face</v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title>{{transaction.customer.companyName}}</v-list-tile-title>
          <v-list-tile-sub-title v-if="customerQbCustomerName">{{customerQbCustomerName}}</v-list-tile-sub-title>
        </v-list-tile-content>
        <v-list-tile-action v-if="verifiedCustomer">
          <v-icon color="green">verified_user</v-icon>
        </v-list-tile-action>
      </v-list-tile>
      <v-list-tile>
        <v-list-tile-avatar>
          <v-icon>money</v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title>{{transaction.vendor.companyName}}</v-list-tile-title>
        </v-list-tile-content>
        <v-list-tile-action v-if="verifiedVendor">
          <v-icon color="green">verified_user</v-icon>
        </v-list-tile-action>
      </v-list-tile>
      <v-subheader>Load Overview</v-subheader>
      <v-list-tile>
        <v-list-tile-avatar>
          <v-icon :color="bookedLoadIcon.color">
            {{bookedLoadIcon.icon}}
          </v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title>
            {{bookedLoadIcon.description}}
          </v-list-tile-title>
        </v-list-tile-content>
      </v-list-tile>
      <v-list-tile>
        <v-list-tile-avatar>
          <v-icon>place</v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title>{{shipper.company ? shipper.company.name : shipper.location.googlePlace.placeName}}</v-list-tile-title>
          <v-list-tile-sub-title>{{shipper.location.googlePlace.formattedAddress}}</v-list-tile-sub-title>
        </v-list-tile-content>
      </v-list-tile>
      <v-list-tile>
        <v-list-tile-avatar>
          <v-icon>outlined_flag</v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title>{{receiver.company ? receiver.company.name : receiver.location.googlePlace.placeName}}</v-list-tile-title>
          <v-list-tile-sub-title>{{receiver.location.googlePlace.formattedAddress}}</v-list-tile-sub-title>
        </v-list-tile-content>
      </v-list-tile>
      <v-list-tile>
        <v-list-tile-avatar>
          <v-icon>share</v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title>{{dispatchCompanyName}}</v-list-tile-title>
          <v-list-tile-sub-title v-if="dispatchQbVendorName">{{dispatchQbVendorName}}</v-list-tile-sub-title>
        </v-list-tile-content>
        <v-list-tile-action v-if="dispatchCompanyVerified">
          <v-icon color="green">verified_user</v-icon>
        </v-list-tile-action>
      </v-list-tile>
      <v-list-tile>
        <v-list-tile-avatar>
          <v-icon>local_shipping</v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title>{{transactionHaulingCompany(transaction).name}}</v-list-tile-title>
          <v-list-tile-sub-title v-if="truckingCompanyQbVendorname">{{truckingCompanyQbVendorname}}</v-list-tile-sub-title>
        </v-list-tile-content>
        <v-list-tile-action v-if="isVendorAssigned">
          <img height="24" width="24"
               src="/static/Quickbooks-Logo.png"/>
        </v-list-tile-action>
        <v-list-tile-action v-else-if="verifiedVendor">
          <v-icon color="green">verified_user</v-icon>
        </v-list-tile-action>
      </v-list-tile>
      <v-list-tile v-if="driverInfo">
        <v-list-tile-avatar>
          <bqa-certificate-icon small :driver="driverInfo"></bqa-certificate-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title>{{driverInfo.displayName}}</v-list-tile-title>
        </v-list-tile-content>
      </v-list-tile>
      <v-list-tile v-if="billOfLading">
        <v-list-tile-avatar>
          <v-icon>attachment</v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <template v-if="billOfLading.name">
            <v-btn class="primary--text mx-0 px-0" flat
                   @click="openBillOfLading(billOfLading.link)"
                   v-if="billOfLading.link">
              {{billOfLading.name}}
            </v-btn>
            <template v-else>
              {{billOfLading.name}}
              <span class="font-italic">
                {{billOfLading.status}}
              </span>
            </template>
          </template>
          <v-list-tile-sub-title v-if="billOfLadingNumber">
            <span class="font-italic">
              {{ $t('terms.bill-of-lading') }} Number: {{billOfLadingNumber}}
            </span>
          </v-list-tile-sub-title>
        </v-list-tile-content>
      </v-list-tile>
      <v-list-tile v-if="directDispatch">
        <v-list-tile-avatar>
          <v-icon>headset_mic</v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title>{{directDispatchDriverName}}</v-list-tile-title>
          <v-list-tile-sub-title v-if="directDispatchQbVendorName">{{directDispatchQbVendorName}}</v-list-tile-sub-title>
        </v-list-tile-content>
      </v-list-tile>

      <v-list-tile v-if="transaction.load.delivery.when">
        <v-list-tile-avatar>

        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title>Delivered {{transaction.load.delivery.animalCount}}</v-list-tile-title>
          <v-list-tile-sub-title class="font-italic">Delivery Weight: {{transaction.load.delivery.animalWeight || transaction.load.delivery.animalWeightPounds}}</v-list-tile-sub-title>
        </v-list-tile-content>
      </v-list-tile>
      <v-list-tile v-if="transaction.load.pickup.when">
        <v-list-tile-avatar>

        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title>Picked Up {{transaction.load.pickup.animalCount}}</v-list-tile-title>
          <v-list-tile-sub-title class="font-italic">Pickup Weight: {{transaction.load.pickup.animalWeight || transaction.load.pickup.animalWeightPounds}}</v-list-tile-sub-title>
        </v-list-tile-content>
      </v-list-tile>
    </v-list>

    <v-card flat class="pb-3 grey lighten-3" v-if="false">
      <v-toolbar flat
                 class="mt-2"
                 color="grey lighten-3"
                 extended extension-height="100">
        <v-toolbar-title >
          Load Progress
        </v-toolbar-title>

        <v-layout column slot="extension" class="white">
          <v-load-progress-stepper :load="transaction.load">

          </v-load-progress-stepper>
        </v-layout>
      </v-toolbar>
    </v-card>


  </v-layout>
</template>

<script>
import API from '@/api';
import _ from 'lodash';

import { txStatus } from '@/types';

import * as Checks from '@/store/permissions';
import companyNamesMixin from '@/mixins/companyNames';
import bookedLoadMixin from '@/mixins/bookedLoadMixin';
import loadGroupMixin from '@/mixins/loadGroupMixin';
import loadTransactionsMixin from '@/mixins/loadTransactionsMixin';
import planningLoadGroupsMixin from '@/mixins/planningLoadGroupsMixin';
import quickbooksMixin from '@/mixins/quickbooksMixin';
import txMixin from '@/mixins/loadTransactionsMixin';
import VLoadProgressStepper from '../LoadProgressStepper';
import VQuickbooksBillingBtn from '../qb/QuickbooksBillingButton';
import AttachPaymentDialog from '../../pages/Companies/AttachPaymentDialog';
import BqaCertificateIcon from '../BqaCertificateIcon';

export default {
  name: 'VLoadTransactionStatusDetails',
  components: { BqaCertificateIcon, AttachPaymentDialog, VQuickbooksBillingBtn, VLoadProgressStepper },
  mixins: [
    bookedLoadMixin,
    companyNamesMixin,
    loadGroupMixin,
    loadTransactionsMixin,
    planningLoadGroupsMixin,
    quickbooksMixin,
    txMixin,
  ],
  props: {
    transaction: {
      type: Object,
      required: true,
    },
    company: {
      type: Object,
      required: true,
    },
    invoice: {
      type: Boolean,
      default: false,
    },
    bill: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    _() {
      return _;
    },
    billOfLading() {
      return _.get(this.transaction, 'load.billOfLading', null);
    },
    billOfLadingNumber() {
      return _.get(this.transaction, 'billOfLadingNumber', null);
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    invoiceTime() {
      return _.get(this.transaction, 'invoice.created', null);
    },
    paymentTime() {
      return _.get(this.transaction, 'payment.timestamp', null);
    },
    receivedPaymentTime() {
      return _.get(this.transaction, 'receivedPayment.timestamp', null);
    },
    driverInfo() {
      return this.transactionDriver(this.transaction);
    },
    loadCustomer() {
      return this.transactionLoadCustomer(this.transaction);
    },
    customer() {
      return this.transactionCustomer(this.transaction);
    },
    vendor() {
      return this.transactionVendor(this.transaction);
    },
    shipper() {
      return this.transactionShipper(this.transaction);
    },
    receiver() {
      return this.transactionReceiver(this.transaction);
    },
    verifiedCustomer() {
      return this.isLoadGroupCompanyVerified(this.customer);
    },
    verifiedVendor() {
      return this.isLoadGroupCompanyVerified(this.vendor);
    },
    isVendorAssigned() {
      return this.isBookedLoadVendorAssigned(this.transaction.load);
    },
    paymentProgressStatusNumber() {
      switch (this.transactionStatus(this.transaction)) {
        case txStatus.OPEN:
          return 1;
        case txStatus.INVOICED:
          return 2;
        case txStatus.PAYMENT_REMITTED:
          return 3;
        case txStatus.PAYMENT_RECEIVED:
          return 4;
        default:
          return 1;
      }
    },

    directDispatch() {
      return this.transactionTruckingCompanyIsOwner(this.transaction);
    },
    directDispatchDriverName() {
      // this company is not the dispatcher. "direct dispatch" is none of their business
      if (this.transactionLoadOwner(this.transaction).id !== this.company.id) {
        return null;
      }

      if (this.directDispatch) {
        // if the attribute is present but set to null, _.get() returns null.
        return _.get(this.transaction, 'load.tacticalInfo.assignedTruckingCompanyDriverName') || 'Driver Not Assigned';
      }

      return 'Load not directly dispatched';
    },
    directDispatchQbVendorName() {
      // this company is not the dispatcher. "direct dispatch" is none of their business
      if (this.transactionLoadOwner(this.transaction).id !== this.company.id) {
        return null;
      }

      const driverUserId = this.transaction.load.delivery.truckingCompanyDriverUserId;
      if (driverUserId) {
        const userId = parseInt(driverUserId, 10);
        const driverEe = _.find(_.get(this.company, 'employees', []), e => parseInt(e.userId, 10) === userId);

        const vendorId = _.get(driverEe, 'quickbooksVendorId');

        if (vendorId) {
          const driverVendorId = parseInt(vendorId, 10);
          const driverVendor = _.find(this.myQbVendors, v => parseInt(v.qbCompanyId, 10) === driverVendorId);
          return _.get(driverVendor, 'selectDisplay');
        }
      }

      return undefined;
    },
    dispatchCompanyName() {
      return this.loadGroupOwner(this.transaction.load.tacticalInfo).name;
    },
    dispatchCompanyVerified() {
      return this.transactionLoadOwner(this.transaction).verified;
    },
    dispatchQbVendorName() {
      const dispatcher = this.transactionLoadOwner(this.transaction);
      // this company is the dispatcher. we are not a vendor in quickbooks
      if (dispatcher.id === this.company.id) {
        return null;
      }

      const dispatcherCompanyId = parseInt(dispatcher.id, 10);
      const vendor = this.findQbVendorByLtnCompanyId(dispatcherCompanyId);
      return _.get(vendor, 'selectDisplay');
    },
    truckingCompanyQbVendorname() {
      const vendor = this.bookedLoadVendorCarrierCompany(this.transaction.load);
      if (vendor) {
        // This load was directly dispatched to QB Vendor. No LTN trucking company profile
        return null;
      }

      const trucking = this.transactionTruckingCompany(this.transaction);
      // this company is the trucker. we are not a vendor in quickbooks
      if (trucking.id === this.company.id) {
        return null;
      }

      const truckingCompanyId = parseInt(trucking.id, 10);
      const mappedVendor = this.findQbVendorByLtnCompanyId(truckingCompanyId);
      return _.get(mappedVendor, 'selectDisplay');
    },

    bookedLoadIcon() {
      return this.bookedLoadProgressIcon(this.transaction.load);
    },
    animalLoss() {
      const pickup = _.get(this.transaction, 'load.pickup.animalCount', null);
      const delivered = _.get(this.transaction, 'load.delivery.animalCount', null);

      if (pickup != null && delivered != null) {
        return delivered - pickup;
      }
      return null;
    },

    customerQbCustomerName() {
      const customer = this.transactionQbCustomer(this.transaction, this.company);
      return _.get(customer, 'selectDisplay');
    },
    qbCustomersLoading() {
      return this.customersStatus[this.company.id] || false;
    },
    myQbCustomers() {
      return this.qbCustomersLoading ? [] : this.quickbooksCustomers(this.company.id);
    },
    qbVendorsLoading() {
      return this.vendorsStatus[this.company.id] || false;
    },
    myQbVendors() {
      return this.qbVendorsLoading ? [] : this.quickbooksVendors(this.company.id);
    },
    canCancelTransaction() {
      const loadOwner = this.transactionLoadOwner(this.transaction);
      const companyAccount = this.findCompanyAccount()(loadOwner.id);

      return this.isLtnAdmin ||
        Checks.accountHasOfficePermission(companyAccount) ||
        Checks.accountHasFinancialPermission(companyAccount) ||
        Checks.accountHasAdminPermission(companyAccount);
    },
  },
  methods: {
    openBillOfLading(link) {
      this.$store.dispatch('lightbox/show', link);
    },
    findQbVendorByLtnCompanyId(companyId) {
      return _.find(this.myQbVendors, qv => parseInt(qv.ltnCompanyId, 10) === companyId);
    },
    openAttachDialog(item, strategy) {
      this.loadItem = item;
      this.attachPaymentStrategy = strategy;

      this.showAttachDialog = true;
    },
    async cancelTransaction() {
      const confirmation = await this.$confirm('Do you really want to cancel this transaction?');
      if (confirmation) {
        try {
          await API.settlements.cancelTransaction(this.transaction.id);

          this.$store.dispatch('loadTransactions/removeTransaction', this.transaction.id);
          this.$emit('reset-selected');
          this.$store.dispatch('applications/setSuccessNotification', {
            message: 'Transaction canceled',
          });
        } catch (error) {
          this.$store.dispatch('applications/setErrorNotification', {
            message: error.message,
          });
        }
      }
    },
  },
  data: () => ({
    tab: null,
    showAttachDialog: false,
    loadItem: null,
    attachPaymentStrategy: null,
    ATTACH_INVOICE: { title: 'Attach Invoice Info', paymentLabel: 'Attach Invoice', buttonLabel: 'Attach', callback: API.settlements.attachInvoice },
    APPLY_PAYMENT: { title: 'Mark Paid', paymentLabel: 'Payment Record Number', buttonLabel: 'Mark Paid', callback: API.settlements.applyPayment },
    ACK_PAYMENT: { title: 'Acknowledge Payment', paymentLabel: 'Payment Record Number', buttonLabel: 'Acknowledge', callback: API.settlements.acknowledgePayment },
  }),
  created() {
    if (this.bill || this.invoice) {
      // well done. you did this right
    } else {
      throw new TypeError('Must specify either bill or invoice property as true');
    }
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/components/InfoWidgets/LoadTransactionStatusDetails.vue